import type { CustomRoleTaxonomySelection } from '@revelio/data-access';

import {
  PreferenceScope,
  UserPreferenceKey,
} from '../../../../shared/user-preferences/types';
import { PreferenceBrowserStorage } from '../../../../shared/user-preferences/utils/preference-session-storage';

/**
 * The single place that decides "what should the adaptive role/skill taxonomy
 * be for this company selection, and should it read as user-submitted?"
 *
 * Called once per reducer transition by `deriveAdaptiveTaxonomies`, so the
 * taxonomy settles in the same step the company selection changes — there is no
 * post-render recompute that could flip the value a render later and refetch
 * every composition query. The caller only reaches these functions once a
 * company is selected and its RICS tree + skill-taxonomy versions have loaded
 * (it pauses the query until then), so the decision is always final: a `value`
 * plus whether it should read as user-submitted. The caller writes it only when
 * it differs from the current filter.
 */
export interface TaxonomyDecision<Value> {
  readonly value: Value;
  readonly isUserSubmitted: boolean;
}

/**
 * The user's explicit (non-adaptive) role taxonomy preference, or `undefined`
 * when the effective preference is adaptive/unset. Read from the single GLOBAL
 * preference.
 */
export const readExplicitRoleTaxonomyPreference = ():
  | CustomRoleTaxonomySelection
  | undefined => {
  const rolePref = PreferenceBrowserStorage.get(PreferenceScope.GLOBAL)[
    UserPreferenceKey.Taxonomies
  ]?.role;
  return rolePref && rolePref !== 'adaptive'
    ? (rolePref as CustomRoleTaxonomySelection)
    : undefined;
};

/**
 * Numeric-taxonomy twin of {@link readExplicitRoleTaxonomyPreference}: the
 * user's explicit numeric id for the given sub-key, or `undefined` when the
 * effective preference is adaptive/unset.
 */
export const readConcreteTaxonomyPreferenceId = (
  prefKey: 'skill' | 'activity'
): number | undefined => {
  const pref = PreferenceBrowserStorage.get(PreferenceScope.GLOBAL)[
    UserPreferenceKey.Taxonomies
  ]?.[prefKey];
  return typeof pref === 'number' ? pref : undefined;
};

export const decideRoleTaxonomy = ({
  currentValue,
  currentIsUserSubmitted,
  isPinned,
  explicitPreference,
  freezeOnSelections,
  computeAdaptiveValue,
}: {
  currentValue: CustomRoleTaxonomySelection;
  currentIsUserSubmitted: boolean | undefined;
  /**
   * An AI-paired taxonomy (`isAiApplied`) or a specific taxonomy opened from
   * a link: keep it, don't recompute.
   */
  isPinned: boolean;
  explicitPreference: CustomRoleTaxonomySelection | undefined;
  /** Plain ROLE selections are present — recomputing would reinterpret them. */
  freezeOnSelections: boolean;
  /** Called only when an adaptive value is actually needed (no version stamp). */
  computeAdaptiveValue: () => CustomRoleTaxonomySelection;
}): TaxonomyDecision<CustomRoleTaxonomySelection> => {
  // The applied role ids are valid only under the paired taxonomy: keep it.
  if (isPinned) {
    return {
      value: currentValue,
      isUserSubmitted: currentIsUserSubmitted ?? false,
    };
  }

  // An explicit, non-adaptive preference wins over adaptive resolution.
  if (explicitPreference) {
    return {
      value: {
        ...explicitPreference,
        version: explicitPreference.version ?? currentValue.version,
      },
      isUserSubmitted: true,
    };
  }

  // Roles are explicitly selected: keep the value, only clear a stale flag so
  // it reads as Adaptive.
  if (freezeOnSelections) {
    return { value: currentValue, isUserSubmitted: false };
  }

  return {
    value: { ...computeAdaptiveValue(), version: currentValue.version },
    isUserSubmitted: false,
  };
};

/**
 * Skill/activity share this decision verbatim: both selections are a plain
 * `{ taxonomyId, version }`, so one generic function serves every
 * numeric-taxonomy filter (role differs — its selection is an object union).
 */
export const decideNumericTaxonomy = <
  TSelection extends { taxonomyId?: number; version?: string },
>({
  currentValue,
  currentIsUserSubmitted,
  isPinned,
  concretePreferenceId,
  freezeOnSelections,
  computeAdaptiveValue,
}: {
  currentValue: TSelection;
  currentIsUserSubmitted: boolean | undefined;
  /**
   * An AI-paired taxonomy (`isAiApplied`) or a specific taxonomy opened from
   * a link: keep it, don't recompute.
   */
  isPinned: boolean;
  /** The user's explicit numeric preference for this taxonomy. */
  concretePreferenceId: number | undefined;
  /** Plain tree selections are present — recomputing would reinterpret them. */
  freezeOnSelections: boolean;
  /** Returns the already-validated adaptive value (no version stamp). */
  computeAdaptiveValue: () => TSelection;
}): TaxonomyDecision<TSelection> => {
  // The applied ids are valid only under the paired taxonomy: keep it.
  if (isPinned) {
    return {
      value: currentValue,
      isUserSubmitted: currentIsUserSubmitted ?? false,
    };
  }

  // A concrete numeric preference wins over adaptive resolution.
  if (concretePreferenceId !== undefined) {
    return {
      value: {
        ...currentValue,
        taxonomyId: concretePreferenceId,
        version: currentValue.version,
      },
      isUserSubmitted: true,
    };
  }

  // Selections are present: keep the value, only clear a stale flag.
  if (freezeOnSelections) {
    return { value: currentValue, isUserSubmitted: false };
  }

  return {
    value: { ...computeAdaptiveValue(), version: currentValue.version },
    isUserSubmitted: false,
  };
};
